import { Alert, Box, Button, TablePagination, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ROWS_PER_PAGE_OPTIONS, type EntityListPagination } from '@/hooks/usePagedQuery';
import { describeApiError } from '@/lib/describeApiError';

export type { EntityListPagination };

export interface EntityListResultsProps {
  /** The list body to render below the result counter (typically a DataTable). */
  children: ReactNode;
  /** Whether the underlying query is loading. Controls the counter text. */
  loading?: boolean;
  /** Whether the underlying query errored. When true, an error Alert replaces the content. */
  isError?: boolean;
  /** Translated error headline, e.g. `t('errorLoading')`. */
  errorText?: string;
  /**
   * The thrown error. Described here rather than by the caller (#2716).
   *
   * This prop used to be `errorDetail?: string` documented as "e.g.
   * error.message" — and since `ApiError.message` is the HTTP status line,
   * every page using it rendered `otsikko: 500 Internal Server Error`. Taking
   * the error itself means callers cannot reintroduce that by hand.
   */
  error?: unknown;
  /** Refetch handler wired to the Alert's refresh action. */
  onRefetch?: () => void;
  /** Translated result-count text, e.g. `t('searchResultCount', { count })`. The count is the caller's `total`. */
  resultCountText: string;
  /** Show the inline "select a status" warning (when no filter is selected). */
  showSelectStatusWarning?: boolean;
  /**
   * Load-more button: whether more rows exist. Only for views that accumulate
   * rows, such as the work-order kanban board, whose column counts are the
   * cards on the board (#2768). Paged lists use `pagination`.
   */
  hasNextPage?: boolean;
  /** Load-more button: the next rows are loading. */
  isFetchingNextPage?: boolean;
  /** Load-more button: handler. */
  onLoadMore?: () => void;
  /**
   * Page number, page size, and total. When set, the load-more button is not rendered.
   */
  pagination?: EntityListPagination;
}

/**
 * Shared chrome around a list body: a result counter (with an optional
 * "select a status" hint), an error Alert with a refresh action, and either
 * page-number pagination or a load-more button. The list body (a `DataTable`)
 * is passed as `children` (#878, #2736).
 */
export function EntityListResults({
  children,
  loading = false,
  isError = false,
  errorText,
  error,
  onRefetch,
  resultCountText,
  showSelectStatusWarning = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  pagination,
}: EntityListResultsProps) {
  const { t: tc } = useTranslation('common');

  if (isError) {
    return (
      <Alert
        severity="error"
        action={
          onRefetch ? (
            <Button color="inherit" size="small" onClick={onRefetch}>
              {tc('errors.refresh')}
            </Button>
          ) : undefined
        }
      >
        {errorText ?? tc('errors.generic')}
        {error !== undefined ? `: ${describeApiError(error, tc, { fallback: tc('errors.generic') })}` : ''}
      </Alert>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {loading ? tc('loading') : resultCountText}
        {!loading && showSelectStatusWarning && (
          <Typography variant="body2" color="warning.main" sx={{ ml: 1 }} component="span">
            — {tc('selectStatus')}
          </Typography>
        )}
      </Typography>

      {children}

      {pagination ? (
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={(_, next) => {
            pagination.onPageChange(next);
          }}
          rowsPerPage={pagination.rowsPerPage}
          rowsPerPageOptions={pagination.rowsPerPageOptions ?? ROWS_PER_PAGE_OPTIONS}
          onRowsPerPageChange={(event) => {
            pagination.onRowsPerPageChange(Number(event.target.value));
          }}
          labelRowsPerPage={tc('pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) =>
            tc('pagination.displayedRows', { from, to, count })
          }
          getItemAriaLabel={(type) => tc(`pagination.${type}`)}
          // The root is a table cell. Outside a table it must be a full-width
          // block, or a narrow screen grows the row instead of wrapping it.
          sx={{
            display: 'block',
            width: '100%',
            '& .MuiTablePagination-toolbar': {
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              rowGap: 1,
              pl: { xs: 1 },
              pr: { xs: 1 },
            },
            '& .MuiTablePagination-spacer': {
              display: { xs: 'none', sm: 'block' },
            },
          }}
        />
      ) : (
        hasNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button variant="outlined" onClick={onLoadMore} disabled={isFetchingNextPage}>
              {isFetchingNextPage ? tc('loading') : tc('loadMore')}
            </Button>
          </Box>
        )
      )}
    </>
  );
}
